import { withDrawalsFund } from '../../../../api/funds';
import i18n from '../../../../i18n';
import { initRNBridge, postToApp, registerRNHandler, type RNMessage } from '../../../../lib/rnBridge';
import { userState } from '../../../../stores/user';

/** 明文交给 App 做 P256 信封和 OPS 签名。H5 不加密、不签名 */
export const SEAL_WITHDRAW_REQUEST = 'WEB_TO_APP_SealWithdraw';
export const SEAL_WITHDRAW_RESPONSE = 'APP_TO_WEB_SealWithdraw';

const FORGOT_ACCOUNT_PATH = '/profile/forgot-account-contact?returnTo=account';
const SEAL_HEADER_KEYS = ['X-Timestamp', 'X-Nonce', 'X-Sign-Algo', 'X-Key-Purpose', 'X-Signature', 'userId'] as const;

const SUBMIT_TIMEOUT_MS = 20000;

export type WithdrawSubmitBody = {
  accountNum: string;
  currency: string;
  payeeBankCode: string;
  payeeBankAccountNum: string;
  subUserID: string;
  amount: number;
  password: string;
};

type SealOkPayload = {
  ok?: boolean;
  message?: string;
  urlPath?: string;
  body?: Record<string, unknown>;
  headers?: Record<string, unknown>;
};

const text = (value: unknown) => String(value ?? '').trim();

const inAppWebView = () => typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;

/** 拦截器常 reject 成 `"code message"` 字符串，拆出 message 给密码层 */
export const withdrawErrorMessage = (error: unknown) => {
  if (error instanceof Error) return text(error.message);
  if (error && typeof error === 'object') {
    const msg = text((error as any).message || (error as any).data?.message);
    if (msg) return msg;
  }
  const raw = text(error);
  if (!raw) return '';
  const matched = raw.match(/^\s*\S+\s+(.+)$/);
  return text(matched?.[1] || raw);
};

const sealHeaders = (raw: unknown) => {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const headers: Record<string, string> = {};
  for (const key of SEAL_HEADER_KEYS) {
    const value = text(source[key]);
    if (!value) return null;
    headers[key] = value;
  }
  return headers;
};

const sealBody = (raw: unknown) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
};

/** 点「忘记密码」：先关密码层，再打开 App 重置页。提交过程中不要调 */
export const openForgetTradePassword = () => {
  initRNBridge();
  postToApp({
    type: 'WEB_TO_APP_OpenNative',
    payload: { nativePath: FORGOT_ACCOUNT_PATH },
    timestamp: Date.now(),
  });
};

const sealThenPost = (body: WithdrawSubmitBody) => {
  initRNBridge();
  const requestId = `seal_${Date.now()}`;

  return new Promise<void>((resolve, reject) => {
    let done = false;
    let posted = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let unsub: (() => void) | undefined;

    const cleanup = () => {
      if (timer !== undefined) clearTimeout(timer);
      unsub?.();
    };

    const finish = (error?: unknown) => {
      if (done) return;
      done = true;
      cleanup();
      if (error) reject(error);
      else resolve();
    };

    const onMessage = (message: RNMessage) => {
      if (done || posted) return;
      if (message?.type !== SEAL_WITHDRAW_RESPONSE) return;
      if (text(message.requestId) !== requestId) return;

      posted = true;
      cleanup();

      const payload = (message.payload || {}) as SealOkPayload;
      if (payload.ok !== true) {
        finish(new Error(text(payload.message)));
        return;
      }

      const headers = sealHeaders(payload.headers);
      const sealedBody = sealBody(payload.body);
      if (!headers || !sealedBody) {
        finish(new Error(''));
        return;
      }

      // 同一份回包只 POST 一次。失败不重试，nonce 作废，需要用户再点确认让 App 重新封包
      withDrawalsFund(sealedBody, { headers, path: payload.urlPath })
        .then(() => finish())
        .catch((error) => finish(error));
    };

    unsub = registerRNHandler(onMessage);
    postToApp({
      type: SEAL_WITHDRAW_REQUEST,
      requestId,
      timestamp: Date.now(),
      payload: {
        password: body.password,
        userId: text(userState.id),
        username: text(userState.userName),
        accountNum: body.accountNum,
        currency: body.currency,
        payeeBankCode: body.payeeBankCode,
        payeeBankAccountNum: body.payeeBankAccountNum,
        subUserID: body.subUserID,
        amount: body.amount,
      },
    });

    timer = setTimeout(() => finish(new Error('')), SUBMIT_TIMEOUT_MS);
  });
};

/**
 * 密码层确认：明文交给 App 封包，再用回包 body + headers POST /2go/fund/withdrawals。
 * 不在 H5 做 P256 / OPS 签名，也不用同一份回包重试。
 */
export const submitWithdrawal = (body: WithdrawSubmitBody) => {
  if (!inAppWebView()) {
    return Promise.reject(new Error(i18n.H5Withdraw.SubmitInAppOnly));
  }
  const password = text(body.password);
  if (!password) return Promise.reject(new Error(i18n.H5Withdraw.PhTradePassword));

  const accountNum = text(body.accountNum);
  const currency = text(body.currency);
  const payeeBankCode = text(body.payeeBankCode);
  const payeeBankAccountNum = text(body.payeeBankAccountNum);
  const subUserID = text(body.subUserID);
  const amount = typeof body.amount === 'number' ? body.amount : Number(body.amount);
  if (!accountNum || !currency || !payeeBankCode || !payeeBankAccountNum || !subUserID || !Number.isFinite(amount)) {
    return Promise.reject(new Error(i18n.H5Withdraw.PasswordVerifyFail));
  }

  return sealThenPost({
    password,
    accountNum,
    currency,
    payeeBankCode,
    payeeBankAccountNum,
    subUserID,
    amount,
  });
};
